import { useState } from 'react';
import styles from '../ManageUserTable/ManageUserTable.module.scss';
import { Headers } from '../useManageUser';
import loaderSvg from '../../../../assets/aica-loader.svg';
import clsx from 'clsx';
import { ManageUserDrawer } from '../../../Company/UploadData/components/ManageUserDrawer';
import { useOrgBasedUsersTable } from './useOrgBasedUsersTable';
import { debtProductsEmpty } from '../../../../assets/hostedassets';
import ManageDealDrawer from '../../../../common/ManageDealDrawer/ManageDealDrawer';
import { COMMENTS_THREAD_SOURCES } from '../../../../store/commentsThread/commentsThread_reducer';
import { USER_TYPE } from '../../../../utils/constants/user';
import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import { SingleSelectDropdownCellRenderer } from '../../../../common/_custom';
import DataTable from '../../../../common/_custom/DataTable/DataTable';
import DataTableFilterDrawer from '../../../../common/_custom/DataTable/components/DataTableFilterDrawer/DataTableFilterDrawer';
import { DataTableColumnType } from '../../../../common/_custom/DataTable/DataTable.types';
import { PillCellRenderer } from '../../../../common/_custom/DataTable/components/CellRenderers/PillCellRenderer/PillCellRenderer';
import { OwnerCellRenderer } from '../../../../common/_custom/DataTable/components/CellRenderers/OwnerCellRenderer';
import { CommentCellRenderer } from '../../../../common/_custom/DataTable/components/CellRenderers/CommentCellRenderer';
import { TextCellRenderer } from '../../../../common/_custom/DataTable/components/CellRenderers/TextCellRenderer';
import { ComponentCellRenderer } from '../../../../common/_custom/DataTable/components/CellRenderers/ComponentCellRenderer/ComponentCellRenderer';
import { NumberCellRenderer } from '../../../../common/_custom/DataTable/components/CellRenderers/NumberCellRenderer/NumberCellRenderer';
import { DatePickerCellRenderer } from '../../../../common/_custom/DataTable/components/CellRenderers/DatePickerCellRenderer/DatePickerCellRenderer';
import { CompanyNameCellRenderer } from '../../../../common/_custom/DataTable/components/CellRenderers/CompanyNameCellRenderer/CompanyNameCellRenderer';
import ActionMenu from '../../../../common/ActionMenu/ActionMenu';
import { UpdateSingleSelectDropdownProps } from '../../../../common/_custom/DataTable/components/CellRenderers/SingleSelectDropdownCellRenderer';
import { MultiSelectDropdownCellRenderer } from '../../../../common/_custom/DataTable/components/CellRenderers/MultiSelectDropdownCellRenderer/MultiSelectDropdownCellRenderer';
import { setCustomFilterDrawer, updateCompany } from '../../../../store/investor/action';
import { getPlatformUserId, openUserSummonTab } from '../../../../utils/utils';
import { fetchInternalSummonAicaData, InternalSummonAicaDataPayload } from '../../../../store/user/actions';
import TopRightArrowIcon from '../../../../assets/Svg/TopRightArrowIcon';
import LenderNetworkDrawer from '../LenderNetworkDrawer';

export type OrgUser = {
    userId: string;
    emailId: string;
    name: string;
    mobileNumber: string;
    userType: string;
    organizationId: string;
    phoneVerified: boolean;
    emailVerified: boolean;
    countryCode: string;
};

export type OrgBasedUser = {
    organizationName: string;
    organizationDisplayName: string;
    organizationType: string;
    userData: OrgUser[];
    organizationOwner: string;
    organizationId: string;
    comment?: { text: string; activityDate: string; activityBy: string };
    investmentPreferencesCount: number;
    orgAdditionalDetails: {
        feeAligned: string;
    };
    // Per-PNO ViewConfig columns (profileGrade, sectoralPreference, …) — which keys exist
    // varies by PNO, so every read must tolerate the key being absent.
    customFields?: Record<string, string>;
};

export type OrgBasedUsersTableProps = {
    headers: Headers;
    rows: any;
    setRows: (rows: any) => void;
    isRowsLoading: boolean;
    isSummonUser: boolean;
    actionMenuItems: Record<string, { action: (payload: any, source: string) => void }>;
    category: 'Partner' | 'Lender';
    actionConfig: string[];
    fetchRows: (pageNo: number) => Promise<void>;
    onFiltersApply: (filters: Record<string, any>) => void;
    lenderCompanyCounts?: Map<string, number>;
    /**
     * Render only the shared filter drawer, no table — how the mobile branch hosts it.
     * Same prop name as DealsTable.tsx:25,50 so the two screens read alike.
     */
    filterDrawerOnly?: boolean;
};

const OrgBasedUsersTable = ({
    headers,
    rows,
    setRows,
    isRowsLoading,
    isSummonUser,
    actionMenuItems,
    category,
    actionConfig,
    fetchRows,
    onFiltersApply,
    lenderCompanyCounts,
    filterDrawerOnly = false,
}: OrgBasedUsersTableProps) => {
    const {
        manageUserDrawerState,
        selectedOrgUsers,
        selectedOrgName,
        originalCategory,
        showDealDrawer,
        dealDrawerState,
        isFilterDrawerOpen,
        handleDrawerClose,
        getRowText,
        handleDealDrawerClose,
        handleManageUsers,
        commentsThreadState,
        updateOwner,
        localOwners,
        getSelectedOption,
        handleRowClick,
        handleUsersUpdate,
        columns,
        pinnedColumns,
        filterDrawerColumns,
    } = useOrgBasedUsersTable({
        headers,
        rows,
        setRows,
        isRowsLoading,
        isSummonUser,
        actionMenuItems,
        category,
        actionConfig,
        fetchRows,
        onFiltersApply,
    });
    const dispatch = useAppDispatch();
    const userData = useAppSelector((state) => state.user.userData);
    const platformData = useAppSelector((state) => state.user.domainMetadata);
    const owners = useAppSelector((state) => state.investor.ownerDropdownOptions);
    const [lenderNetworkDrawer, setLenderNetworkDrawer] = useState<{
        open: boolean;
        lenderName: string;
    }>({ open: false, lenderName: '' });

    const filterSourceKey = category === 'Lender' ? 'MANAGE_LENDERS' : 'MANAGE_PARTNERS';

    // DataTable is the only thing that normally mounts this drawer (DataTable.tsx:675-683), so
    // mount it directly instead — gated on `open` as DataTable gates it, which keeps
    // useDynamicFilterOptions from mounting while the drawer is closed.
    if (filterDrawerOnly) {
        return isFilterDrawerOpen ? (
            <DataTableFilterDrawer
                open
                onClose={() => dispatch(setCustomFilterDrawer({ open: false }))}
                columns={filterDrawerColumns}
                onApply={onFiltersApply}
                filterSourceKey={filterSourceKey}
            />
        ) : null;
    }

    const enrichedRows =
        category === 'Lender' && lenderCompanyCounts && lenderCompanyCounts.size > 0
            ? (rows || []).map((row: OrgBasedUser) => ({
                  ...row,
                  companyCount: lenderCompanyCounts.get(row.organizationName) ?? 0,
              }))
            : rows;

    const componentRegistry = ({ row, column, actionMenuItems, handleManageUsers, isSummonUser }) => {
        if (column.componentName === 'ActionMenu') {
            return (
                <div>
                    <ActionMenu
                        triggerPayload={{
                            organizationId: row?.organizationId,
                            organizationName: row?.organizationName,
                            selectedUsers: row?.userData,
                            userId: row?.userData?.[0]?.userId,
                            userType: row?.userData?.[0]?.userType,
                            organizationDisplayName: row?.organizationDisplayName,
                        }}
                        rowData={{
                            mainConfig: {
                                actionConfig,
                            },
                        }}
                        actionMenu={{
                            ...actionMenuItems,
                            'Manage User': {
                                action: (payload) => {
                                    const heading = payload?.organizationDisplayName || payload?.organizationName;
                                    handleManageUsers(payload?.selectedUsers, payload?.organizationId, heading);
                                },
                            },
                        }}
                        source="MANAGE_USERS"
                        disabled={isSummonUser}
                        usePortal={true}
                    />
                </div>
            );
        }
        if (column.componentName === 'CompanyCount') {
            const count = row?.companyCount ?? 0;
            const hasCompanies = count > 0;
            return (
                <div
                    onClick={(e) => {
                        e.stopPropagation();
                        if (hasCompanies) setLenderNetworkDrawer({ open: true, lenderName: row?.organizationName });
                    }}
                    className={`${styles.CompanyCountCell} ${!hasCompanies ? styles.CompanyCountCellDisabled : ''}`}
                >
                    <TopRightArrowIcon fill={hasCompanies ? 'var(--primary-purple)' : 'var(--label-color)'} width={14} height={14} />
                    <span className={`${styles.CompanyCountText} ${hasCompanies ? styles.CompanyCountActive : styles.CompanyCountEmpty}`}>
                        {hasCompanies ? `${count} Companies` : 'None'}
                    </span>
                </div>
            );
        }
        return null;
    };

    const updateSingleSelectDropdown = ({ row, option, column, handlers }: UpdateSingleSelectDropdownProps) => {
        const field = column.field;
        const value = option?.value;
        const orgId = row.organizationId;

        updateLocalSingleSelectDropdown(field, value, orgId);

        if (field.includes('customFields')) {
            const customFieldKey = field.split('.')[1];
            // Start with the selected custom field value
            const customFields = {
                [customFieldKey]: value,
            };
            // Merge in any config-driven onChangeUpdateFields, but never overwrite the user's selection
            if (column.onChangeUpdateFields) {
                Object.entries(column.onChangeUpdateFields).forEach(([k, v]) => {
                    if (k.startsWith('customFields.')) {
                        const fieldKey = k.split('.')[1];
                        if (fieldKey !== customFieldKey) {
                            customFields[fieldKey] = v;
                        }
                    }
                });
            }
            dispatch(
                updateCompany(
                    {
                        orgId,
                        customFields,
                    },
                    handlers,
                ),
            );
            return;
        }
        dispatch(
            updateCompany(
                {
                    orgId,
                    [field]: value,
                },
                handlers,
            ),
        );
    };

    const updateMultiSelectDropdown = ({ row, selectedValues, column, handlers }) => {
        const field = column.field;
        const orgId = row.organizationId;
        setRows((prev) =>
            prev.map((r) => {
                if (r.organizationId !== orgId) return r;
                if (field.includes('customFields')) {
                    return { ...r, customFields: { ...r.customFields, [field.split('.')[1]]: selectedValues } };
                }
                return { ...r, [field]: selectedValues };
            }),
        );
        if (field.includes('customFields')) {
            dispatch(updateCompany({ orgId, customFields: { [field.split('.')[1]]: selectedValues } }, handlers));
        } else {
            dispatch(updateCompany({ orgId, [field]: selectedValues }, handlers));
        }
    };

    const updateLocalSingleSelectDropdown = (field, value, orgId) => {
        setRows((prev) => {
            const newRows = prev.map((row) => {
                if (row.organizationId === orgId) {
                    return { ...row, [field]: value };
                }
                return row;
            });
            return newRows;
        });
    };

    const updateCustomFieldStatus = (row, option, column, handlers) => {
        const field = column.field;
        const orgId = row.organizationId;
        const value = option?.value !== undefined ? option.value : option;

        setRows((prev) => {
            const newRows = prev.map((r) => {
                if (r.organizationId !== orgId) {
                    return r;
                }
                if (field.includes('customFields')) {
                    return {
                        ...r,
                        customFields: {
                            ...r.customFields,
                            [field.split('.')[1]]: value,
                        },
                    };
                }
                return { ...r, [field]: value };
            });
            return newRows;
        });

        // Call API to update the backend
        if (field.includes('customFields')) {
            const customFieldKey = field.split('.')[1];
            dispatch(
                updateCompany(
                    {
                        orgId,
                        customFields: {
                            [customFieldKey]: value,
                        },
                    },
                    handlers,
                ),
            );
            return;
        }

        dispatch(
            updateCompany(
                {
                    orgId,
                    [field]: value,
                },
                handlers,
            ),
        );
    };

    const summonInternalUser = (payload: any) => {
        const platformUserId = getPlatformUserId();
        const summonPayload: InternalSummonAicaDataPayload = {
            parentOrgId: userData?.investorOrgId as string,
            parentEmailId: userData.investorPrimaryEmail,
            userId: payload?.userId,
            userType: payload?.userType,
            requesterUserId: platformUserId,
        };
        dispatch(
            fetchInternalSummonAicaData(summonPayload, {
                onSuccess: (userData) => {
                    openUserSummonTab({ ...userData, orgId: platformData?.investorOrgId });
                },
            }),
        );
    };

    // Custom cell renderers
    const customCellRenderers = {
        [DataTableColumnType.TEXT]: (props) => <TextCellRenderer {...props} />,
        [DataTableColumnType.COMPANY_NAME_CTA]: (props) => {
            return (
                <CompanyNameCellRenderer
                    {...props}
                    dynamicProps={{
                        handleRowClick: ({ event, row }) => {
                            event?.stopPropagation();
                            event?.preventDefault();
                            summonInternalUser({
                                userId: row?.userData?.[0]?.userId,
                                userType: row?.userData?.[0]?.userType,
                            });
                        },
                    }}
                />
            );
        },
        [DataTableColumnType.NUMBER]: (props) => (
            <NumberCellRenderer
                {...props}
                dynamicProps={{
                    updateCustomFieldStatus,
                    isDisabled:
                        isSummonUser || !props?.row?.organizationId || (props?.column?.disable && !props?.row?._isCollapsibleRow) || false,
                }}
            />
        ),
        [DataTableColumnType.OWNER]: (props) => (
            <OwnerCellRenderer
                {...props}
                dynamicProps={{
                    getSelectedOption,
                    updateOwner: (payload) => {
                        if (payload && payload.row && payload.value !== undefined) {
                            // Find the owner object to get customSortName
                            const selectedOwner = owners.find((owner) => owner.userId === payload.value);
                            const ownerName = selectedOwner?.customSortName || selectedOwner?.name || payload.label;

                            updateOwner(payload.row.organizationId, {
                                name: ownerName,
                                userId: payload.value,
                                activityBy: userData.userName,
                                activityDate: new Date().toISOString(),
                                ownerType: payload.headerId || 'owner',
                            });
                        }
                    },
                    isDisabled: isSummonUser,
                }}
            />
        ),
        [DataTableColumnType.COMMENT]: (props) => (
            <CommentCellRenderer
                {...props}
                dynamicProps={{
                    getOrgId: (row) => row.organizationId,
                    source: 'MANAGE_PARTNERS_LENDERS',
                    isDealView: false,
                    borrowerName: (row) => row.organizationName,
                }}
            />
        ),
        [DataTableColumnType.PILL]: ({ value }) => {
            const isActive = value && value > 0;
            const label = isActive ? `${value} active` : 'Not active';
            return <PillCellRenderer label={label} isActive={isActive} />;
        },
        [DataTableColumnType.COMPONENT]: (props) => (
            <ComponentCellRenderer
                {...props}
                dynamicProps={{
                    componentRenderFnRegistry: ({ row, column }) =>
                        componentRegistry({ row, column, actionMenuItems, handleManageUsers, isSummonUser }),
                }}
            />
        ),
        [DataTableColumnType.DATE_PICKER]: (props) => (
            <DatePickerCellRenderer
                {...props}
                dynamicProps={{
                    updateCustomFieldStatus,
                    isDisabled: isSummonUser || !props?.row?.organizationId || false,
                }}
            />
        ),
        [DataTableColumnType.MULTI_SELECT_DROPDOWN]: (props) => (
            <MultiSelectDropdownCellRenderer
                {...props}
                dynamicProps={{
                    updateMultiSelectDropdown: updateMultiSelectDropdown,
                    isDisabled: isSummonUser || !props?.row?.organizationId || false,
                }}
            />
        ),
        [DataTableColumnType.SINGLE_SELECT_DROPDOWN]: (props) => (
            <SingleSelectDropdownCellRenderer
                {...props}
                dynamicProps={{
                    updateSingleSelectDropdown,
                    isDisabled: props?.column?.disable || false,
                }}
            />
        ),
    };

    return (
        <div className={styles.TableContainer}>
            {manageUserDrawerState && (
                <ManageUserDrawer
                    isOrgDrawer={true}
                    onClose={handleDrawerClose}
                    selectedOrgName={selectedOrgName}
                    selectedUsers={selectedOrgUsers}
                    parentCategory={originalCategory}
                    onUsersUpdate={handleUsersUpdate}
                />
            )}
            <DataTable
                columns={columns}
                rows={enrichedRows}
                loading={isRowsLoading}
                customCellRenderers={customCellRenderers}
                pinnedColumns={pinnedColumns}
                totalRowCount={Array.isArray(enrichedRows) ? enrichedRows.length : 0}
                emptyState={<div>No data found</div>}
                noSearchResults={<div>No results found</div>}
                valueGetterMap={{
                    userData: (row) => (Array.isArray(row.userData) && row.userData.length > 0 ? row.userData.map((u) => u.name) : []),
                    comment: (row) => row.comment || null,
                }}
                onRowClick={(e, row) => {
                    e.preventDefault?.();
                    e.stopPropagation?.();

                    const clickedCell = (e.target as HTMLElement).closest('td');
                    if (!clickedCell) return;

                    const cellIndex = Array.from(clickedCell.parentNode?.children || []).indexOf(clickedCell);
                    const column = columns[cellIndex];

                    if (column) handleRowClick(column, row, e);
                }}
                filterSourceKey={filterSourceKey}
                onFiltersApply={onFiltersApply}
            />
            {showDealDrawer && dealDrawerState.drawerSource !== 'ProfileButton' && (
                <ManageDealDrawer source={'OrgUsersTable'} onClose={handleDealDrawerClose} />
            )}
            {lenderNetworkDrawer.open && (
                <LenderNetworkDrawer
                    open={lenderNetworkDrawer.open}
                    lenderName={lenderNetworkDrawer.lenderName}
                    pnoId={userData?.investorOrgId || ''}
                    onClose={() => setLenderNetworkDrawer({ open: false, lenderName: '' })}
                />
            )}
        </div>
    );
};

export default OrgBasedUsersTable;
